import * as React from 'react';
import { JobNotificationDto, FileUploadStatus } from 'src/models/interfaces/INotification';
import { getUnreadNotifications, markNotificationAsRead, downloadExportJob } from 'src/services/pages/Workload/notificationApi';
import { useErrorSetter } from 'src/context/ErrorProvider';
import { StatusTypes } from 'src/models/types/StatusTypes';
import { createNotificationConnection } from 'src/services/signalRService';

interface INotificationContext {
  pendingJobId: number | null;
  setPendingJobId: (jobId: number | null) => void;
  fetchUnread: () => Promise<void>;
}

const NotificationContext = React.createContext<INotificationContext>({
  pendingJobId: null,
  setPendingJobId: () => {},
  fetchUnread: async () => {},
});

function toStatusType(status: FileUploadStatus): StatusTypes {
  switch (status) {
    case FileUploadStatus.Completed:  return 'success';
    case FileUploadStatus.Failed:     return 'error';
    case FileUploadStatus.Processing: return 'info';
    default:                          return 'warning';
  }
}

function buildDescription(n: JobNotificationDto): React.JSX.Element | undefined {
  if (n.totalRows == null && !n.errorMessage) return undefined;
  return (
    <span>
      {n.totalRows != null && (
        <span style={{ display: 'block' }}>
          {`${n.totalRows} rows · ${n.successfulRows ?? 0} ok · ${n.failedRows ?? 0} failed`}
        </span>
      )}
      {n.errorMessage && (
        <span style={{ display: 'block' }}>{n.errorMessage}</span>
      )}
    </span>
  );
}

export const NotificationProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [pendingJobId, setPendingJobId] = React.useState<number | null>(null);
  const addError = useErrorSetter();

  const handleNotification = React.useCallback((n: JobNotificationDto) => {
    if (n.jobType?.startsWith('WorkloadExport') && n.status === FileUploadStatus.Completed) {
      downloadExportJob(n.fileUploadJobId, n.fileName).catch(() => {});
    }

    addError({
      typeMessage: toStatusType(n.status),
      title: n.jobType ?? 'Job',
      subtitle: n.fileName,
      dateTime: n.completedOn ? new Date(n.completedOn) : new Date(n.createdOn),
      description: buildDescription(n),
    });
    markNotificationAsRead(n.id).catch(() => {});
  }, [addError]);

  const fetchUnread = React.useCallback(async () => {
    try {
      const data = await getUnreadNotifications();
      data.forEach(handleNotification);
    } catch {
      // Non-blocking: failed notification fetch should not break the app
    }
  }, [handleNotification]);

  React.useEffect(() => {
    const connection = createNotificationConnection();

    connection.on('ReceiveNotification', (notification: JobNotificationDto) => {
      handleNotification(notification);
    });

    connection.start().catch(() => {});

    return () => {
      connection.stop().catch(() => {});
    };
  }, [handleNotification]);

  return (
    <NotificationContext.Provider value={{ pendingJobId, setPendingJobId, fetchUnread }}>
      {children}
    </NotificationContext.Provider>
  );
};

export function useNotifications(): INotificationContext {
  return React.useContext(NotificationContext);
}

export default NotificationContext;
